import PageShell from "../../../design-system/components/PageShell/PageShell";
import Text from "../../../design-system/primitives/components/Text/Text";
import Button from "../../../design-system/primitives/components/Button/Button";
import type { ShowroomEntryListItem } from "../../../api/showroom";
import type { WorkshopSettings } from "../../../api/workshop";
import { styles } from "./sparkShowroomOverview.css";
import {
  ShowroomEntryGrid,
  ShowroomEntryPreview,
} from "./ShowroomEntryPreview";

/**
 * Renders a showroom overview with data.
 *
 * The header carries up to two actions besides the title and the back button:
 * the public showroom (only for a workshop that has one) and the team's own
 * entry — "Hochladen" until it exists, "Mein Beitrag" afterwards. Both are
 * absent for anyone without a small team of their own, e.g. a teacher.
 */
export const SparkShowroomOverviewWithData = ({
  entries,
  otherSharedEntries = [],
  unseenByEntryId = {},
  workshopSettings,
  hasExistingShowroomEntry = false,
  publicShowroomButtonTarget,
  uploadEditButtonTarget,
}: {
  entries: ShowroomEntryListItem[];
  otherSharedEntries?: ShowroomEntryListItem[];
  unseenByEntryId?: Record<string, number>;
  workshopSettings: WorkshopSettings;
  hasExistingShowroomEntry?: boolean;
  publicShowroomButtonTarget?: string | null;
  uploadEditButtonTarget?: string | null;
}) => {
  return (
    <PageShell
      surface="dark"
      titleButton={{ icon: "arrow-left", ariaLabel: "Zurück", to: ".." }}
      titleSlot={
        <div className={styles.headerRow}>
          <Text variant="h4">Showroom</Text>

          <div className={styles.actions}>
            {publicShowroomButtonTarget && (
              <Button
                title="Zum Event Showroom"
                size="large"
                color="neutral"
                variant="secondary"
                // Another host, so a plain link rather than a route change.
                href={publicShowroomButtonTarget}
              />
            )}
            {uploadEditButtonTarget && (
              <Button
                title={hasExistingShowroomEntry ? "Mein Beitrag" : "Hochladen"}
                size="large"
                color="accent"
                iconRight={hasExistingShowroomEntry ? "pencil" : "upload"}
                to={uploadEditButtonTarget}
              />
            )}
          </div>
        </div>
      }
    >
      {entries.length === 0 ? (
        <div className={styles.emptyText}>
          <p>Noch keine Projekte</p>
          {/* Only true for someone who actually has the upload button beside
              this text: a teacher has no team to upload with, and the demo has
              no Lernreise to upload into. */}
          {uploadEditButtonTarget && (
            <p>
              Über die Hochladen-Funktion kannst du ein Projekt zum Showroom
              hinzufügen.
            </p>
          )}
        </div>
      ) : (
        <ShowroomEntryGrid>
          {entries.map((entry) => (
            <ShowroomEntryPreview
              key={entry.id}
              entry={entry}
              unseenCount={unseenByEntryId[entry.id]}
              workshopSettings={workshopSettings}
            />
          ))}
        </ShowroomEntryGrid>
      )}

      {otherSharedEntries.length > 0 && (
        <section>
          <h2 className={styles.otherEntriesHeading}>
            Projekte anderer Lernreisen
          </h2>
          <ShowroomEntryGrid>
            {otherSharedEntries.map((entry) => (
              <ShowroomEntryPreview
                key={entry.id}
                entry={entry}
                unseenCount={unseenByEntryId[entry.id]}
                workshopSettings={workshopSettings}
              />
            ))}
          </ShowroomEntryGrid>
        </section>
      )}
    </PageShell>
  );
};
